import { create } from 'zustand';

import {
  formatPrice as formatPriceUtil,
  getCurrencySymbol as getCurrencySymbolUtil,
  multiplyPrice,
  roundByCurrency,
  sumPrices,
} from '@/shared/utils/priceUtils';

import {
  CreateOrderProduct,
  CreateOrderReceiverInfo,
  CreateOrderRequest,
  CurrencyCode,
  ProductPriceInfo,
  SelectedProduct,
  User,
} from '../types/orderTypes';

interface OrderCreateStore {
  selectedUser: User | null;
  selectedProducts: SelectedProduct[];
  receiverInfo: CreateOrderReceiverInfo;
  currencyCode: CurrencyCode;
  finalPaymentAmount: number;

  setSelectedUser: (user: User | null) => void;
  addProduct: (product: SelectedProduct) => void;
  removeProduct: (optionUuid: string) => void;
  updateProductQuantity: (optionUuid: string, quantity: number) => void;
  clearProducts: () => void;
  isProductSelected: (optionUuid: string) => boolean;
  getTotalQuantity: () => number;
  updateReceiverInfo: (
    field: keyof CreateOrderReceiverInfo,
    value: string,
  ) => void;
  setReceiverInfoFromUser: (user: User) => void;
  setCurrencyCode: (currency: CurrencyCode) => void;
  setFinalPaymentAmount: (amount: number) => void;
  calculateFinalPaymentAmount: () => void;
  getProductPrice: (
    productPriceList: ProductPriceInfo[],
    currencyCode: CurrencyCode,
  ) => number;
  getCurrencySymbol: () => string;
  getNationCodeFromCurrency: (currencyCode: CurrencyCode) => string;
  formatPrice: (price: number) => string;
  getMinimumQuantity: (optionUuid: string) => number;
  getProductsForApi: () => CreateOrderProduct[];
  getOrderRequestData: () => Omit<CreateOrderRequest, 'finalPaymentAmount'> & {
    finalPaymentAmount: number;
  };
  resetOrderData: () => void;
}

const initialReceiverInfo: CreateOrderReceiverInfo = {
  companyName: '',
  companyEmail: '',
  receiverName: '',
  postalCode: '',
  addressDetail: '',
  phoneNumber: '',
};

export const useOrderCreateStore = create<OrderCreateStore>((set, get) => ({
  selectedUser: null,
  selectedProducts: [],
  receiverInfo: initialReceiverInfo,
  currencyCode: 'KRW',
  finalPaymentAmount: 0,

  setSelectedUser: (user: User | null) => {
    set({ selectedUser: user });
  },

  addProduct: (product: SelectedProduct) => {
    set((state) => {
      if (
        state.selectedProducts.some((p) => p.optionUuid === product.optionUuid)
      ) {
        return state;
      }

      const productWithMinQuantity = {
        ...product,
        quantity: Math.max(product.quantity, product.minimumOrderQuantity),
      };

      const newProducts = [...state.selectedProducts, productWithMinQuantity];
      return {
        selectedProducts: newProducts,
      };
    });
    get().calculateFinalPaymentAmount();
  },

  removeProduct: (optionUuid: string) => {
    set((state) => ({
      selectedProducts: state.selectedProducts.filter(
        (p) => p.optionUuid !== optionUuid,
      ),
    }));
    get().calculateFinalPaymentAmount();
  },

  updateProductQuantity: (optionUuid: string, quantity: number) => {
    const product = get().selectedProducts.find(
      (p) => p.optionUuid === optionUuid,
    );
    if (!product) return;

    const finalQuantity = Math.max(quantity, product.minimumOrderQuantity);

    set((state) => ({
      selectedProducts: state.selectedProducts.map((p) =>
        p.optionUuid === optionUuid ? { ...p, quantity: finalQuantity } : p,
      ),
    }));
    get().calculateFinalPaymentAmount();
  },

  clearProducts: () => {
    set({ selectedProducts: [] });
    get().calculateFinalPaymentAmount();
  },

  isProductSelected: (optionUuid: string) => {
    return get().selectedProducts.some((p) => p.optionUuid === optionUuid);
  },

  getTotalQuantity: () => {
    return get().selectedProducts.reduce(
      (total, product) => total + product.quantity,
      0,
    );
  },

  updateReceiverInfo: (field: keyof CreateOrderReceiverInfo, value: string) => {
    set((state) => ({
      receiverInfo: {
        ...state.receiverInfo,
        [field]: value,
      },
    }));
  },

  setReceiverInfoFromUser: (user: User) => {
    set((state) => ({
      receiverInfo: {
        ...state.receiverInfo,
        companyName: user.companyName,
        companyEmail: user.email,
      },
    }));
  },

  setCurrencyCode: (currency: CurrencyCode) => {
    set({ currencyCode: currency });
    get().calculateFinalPaymentAmount();
  },

  setFinalPaymentAmount: (amount: number) => {
    set({ finalPaymentAmount: amount });
  },

  getNationCodeFromCurrency: (currencyCode: CurrencyCode) => {
    return currencyCode === 'USD' ? 'US' : 'KR';
  },

  getProductPrice: (
    productPriceList: ProductPriceInfo[],
    currencyCode: CurrencyCode,
  ) => {
    const nationCode = get().getNationCodeFromCurrency(currencyCode);
    const priceInfo = productPriceList.find(
      (price) => price.nationCode2 === nationCode,
    );
    return priceInfo ? priceInfo.totalPrice : 0;
  },

  getCurrencySymbol: () => {
    return getCurrencySymbolUtil(get().currencyCode);
  },

  formatPrice: (price: number) => {
    return formatPriceUtil(price, get().currencyCode);
  },

  getMinimumQuantity: (optionUuid: string) => {
    const product = get().selectedProducts.find(
      (p) => p.optionUuid === optionUuid,
    );
    return product ? product.minimumOrderQuantity : 1;
  },

  calculateFinalPaymentAmount: () => {
    const state = get();
    const products = state.selectedProducts;
    const { currencyCode } = state;

    const productTotals = products.map((product) => {
      const productPrice = state.getProductPrice(
        product.productPriceList,
        currencyCode,
      );
      return multiplyPrice(productPrice, product.quantity, currencyCode);
    });

    const totalAmount = sumPrices(productTotals, currencyCode);

    set({ finalPaymentAmount: totalAmount });
  },

  getProductsForApi: () => {
    return get().selectedProducts.map((product) => ({
      productOptionUuid: product.optionUuid,
      quantity: product.quantity,
    }));
  },

  getOrderRequestData: () => {
    const state = get();
    const roundedAmount = roundByCurrency(
      state.finalPaymentAmount,
      state.currencyCode,
    );

    return {
      currencyCode: state.currencyCode,
      finalPaymentAmount: roundedAmount,
      receiverInfo: state.receiverInfo,
      productOptionList: state.getProductsForApi(),
    };
  },

  resetOrderData: () => {
    set({
      selectedUser: null,
      selectedProducts: [],
      receiverInfo: initialReceiverInfo,
      currencyCode: 'KRW',
      finalPaymentAmount: 0,
    });
  },
}));
